import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useLocation, useHistory } from 'react-router-dom';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  Modal,
  Button,
  SecondaryButton,
  PrimaryButton,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';
import logo from '../../../../assets/logo.svg';

import css from './TopbarDesktop.module.css';
import { getUserType } from '../../../../util/genericHelper';
import { USER_TYPES } from '../../../../util/enums';
import Icons from '../../../../components/Icons/Icons';
import creditImg from '../../../../assets/credit.png';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.topbarLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };
  const userType = getUserType(currentUser);
  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      {userType === USER_TYPES.FIRM ? (
        <MenuContent className={css.profileMenuContent}>
          <MenuItem key="FavoriteListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavoriteListingsPage'))}
              name="FavoriteListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.favoriteListingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="SellerSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SellerSettingsPage'))}
              name="SellerSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.sellerSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      ) : (
        <MenuContent className={css.profileMenuContent}>
          <MenuItem key="FavoriteListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavoriteListingsPage'))}
              name="FavoriteListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.favoriteListingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ManageListingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.yourListingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      )}
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    onManageDisableScrolling,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [showListingApprovalModal, setShowListingApprovalModal] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Check URL query param ?type=user or ?type=listing and open corresponding modal
  useEffect(() => {
    if (!location?.search) {
      setShowListingApprovalModal(false);
      setShowUserProfileModal(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const type = params.get('type');

    if (type === 'listing') {
      setShowListingApprovalModal(true);
      setShowUserProfileModal(false);
    } else if (type === 'user') {
      setShowUserProfileModal(true);
      setShowListingApprovalModal(false);
    } else {
      setShowListingApprovalModal(false);
      setShowUserProfileModal(false);
    }
  }, [location.search]);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );

  const clearTypeQueryParam = () => {
    const params = new URLSearchParams(location.search);
    params.delete('type');
    history.replace({
      ...location,
      search: params.toString() ? `?${params.toString()}` : '',
    });
  };

  const handleCloseListingModal = () => {
    setShowListingApprovalModal(false);
    clearTypeQueryParam();
    console.log('Closing listing approval modal');
  };

  const handleCloseUserModal = () => {
    setShowUserProfileModal(false);
    clearTypeQueryParam();
    console.log('Closing user profile review modal');
  };

  return (
    <nav className={classes}>
      <div className={css.fixedWidthContainer}>
        <div className={css.leftNav}> {searchFormMaybe}</div>
        <div className={css.middleNav}>
          {/* <LinkedLogo
            className={css.logoLink}
            layout="desktop"
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
            linkToExternalSite={config?.topbar?.logoLink}
          /> */}

          <NamedLink name="LandingPage" className={css.logoLink}>
            <img src={logo} alt="logo" />
          </NamedLink>
        </div>
        <div className={css.rightNav}>
          <div className={css.topbarDesktopRight}>
            <CustomLinksMenu
              currentPage={currentPage}
              customLinks={customLinks}
              intl={intl}
              hasClientSideContentReady={
                authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
              }
            />

            {inboxLinkMaybe}
            {profileMenuMaybe}
            {signupLinkMaybe}
            {loginLinkMaybe}

            {/* <Button onClick={handleOpen}>Open</Button> */}
          </div>
        </div>

        {/* Listing approval modal (triggered by ?type=listing) */}
        <Modal
          isOpen={showListingApprovalModal}
          onClose={handleCloseListingModal}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
          isSmsSetupModal={true}
          hideCloseButton={true}
        >
          <div className={css.smsSetupContent}>
            <div className={css.modalIcon}>
              <Icons name="message" />
            </div>
            <div className={css.smsSetupInfo}>
              <h2>Your listing has been sent for approval</h2>
              <p>
                We’re reviewing your listing to make sure it meets our marketplace guidelines before
                it goes live.
              </p>
              <ul>
                <li>
                  <Icons name="checkIcon" /> Most listings are reviewed within a short time.
                </li>
                <li>
                  <Icons name="checkIcon" /> You’ll receive a notification as soon as your listing
                  is approved.
                </li>
                <li>
                  <Icons name="checkIcon" /> While it’s pending, you can continue managing your
                  other listings.
                </li>
              </ul>
              <p>
                <Icons name="infoCircle" />
                Approval times can vary based on review volume. If we need any changes, we’ll
                contact you using the details in your profile.
              </p>
              <div className={css.smsSetupActions}>
                <SecondaryButton onClick={handleCloseListingModal}>
                  Back to listings
                </SecondaryButton>
                <PrimaryButton onClick={handleCloseListingModal}>
                  View listing status <Icons name="rightArrow" />
                </PrimaryButton>
              </div>
            </div>
          </div>
        </Modal>

        {/* User profile review modal (triggered by ?type=user) */}
        <Modal
          isOpen={showUserProfileModal}
          onClose={handleCloseUserModal}
          usePortal
          onManageDisableScrolling={onManageDisableScrolling}
          isSmsSetupModal={true}
          hideCloseButton={true}
        >
          <div className={css.smsSetupContent}>
            <div className={css.smsSetupInfoHead}>
              <h2>Your profile is under review</h2>
            </div>

            <div className={css.smsSetupInfo}>
              <div className={css.keyFeaturesBlock}>
                <div className={css.keyFeaturesIcon}>
                  <Icons name="clock" />
                </div>
                <div className={css.keyFeaturesInfo}>
                  <h2>
                    Quick turnaround <span>Fast</span>
                  </h2>
                  <p>
                    Our team is reviewing your information now. Most profile approvals are completed
                    within a short time.
                  </p>
                </div>
              </div>

              <div className={css.keyFeaturesBlock}>
                <div className={css.keyFeaturesIcon}>
                  <Icons name="document" />
                </div>
                <div className={css.keyFeaturesInfo}>
                  <h2>
                    No extra documents needed <span>Easy</span>
                  </h2>
                  <p>
                    You’ve already submitted everything required — just sit back and relax while we
                    complete the verification.
                  </p>
                </div>
              </div>

              <div className={css.keyFeaturesBlock}>
                <div className={css.keyFeaturesIcon}>
                  <Icons name="efficiency" />
                </div>
                <div className={css.keyFeaturesInfo}>
                  <h2>
                    Stay ahead <span>Progress</span>
                  </h2>
                  <p>
                    Once approved, you’ll be able to move forward faster — including booking
                    viewings and securing the property.
                  </p>
                </div>
              </div>

              <div className={css.smsSetupActions}>
                <SecondaryButton onClick={handleCloseUserModal}>Close</SecondaryButton>
                {/* <PrimaryButton>Continue</PrimaryButton> */}
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
